import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import {
  BrandRouterLinkDirective,
  EmptyStateComponent,
  IPatientBlockFilters,
  TypesenseSearchService,
} from '@principle-theorem/ng-principle-shared';
import {
  MOMENT_DATEPICKER_PROVIDERS,
  NgCommonModule,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import { ITypesensePatientWithRef } from '@principle-theorem/principle-core';
import { PatientStatus } from '@principle-theorem/principle-core/interfaces';
import { DATE_TIME_FORMAT, shareReplayCold } from '@principle-theorem/shared';
import { BehaviorSubject, type Observable, of } from 'rxjs';
import { debounceTime, map, startWith, tap } from 'rxjs/operators';

@Component({
  selector: 'pr-leads-list',
  templateUrl: './leads-list.component.html',
  styleUrls: ['./leads-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
  imports: [
    NgCommonModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressBarModule,
    EmptyStateComponent,
    BrandRouterLinkDirective,
  ],
})
export class LeadsListComponent {
  private readonly _search = inject(TypesenseSearchService);

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  searchCtrl = new TypedFormControl<string>('');
  loading$ = new BehaviorSubject<boolean>(true);
  leads$: Observable<ITypesensePatientWithRef[]>;
  recordCount$: Observable<number>;
  hasResults$: Observable<boolean>;
  displayedColumns = ['name', 'phone', 'email', 'created'];

  constructor() {
    const search$ = this.searchCtrl.valueChanges.pipe(
      debounceTime(300),
      tap(() => this.loading$.next(true)),
      startWith('')
    );

    const filters$: Observable<IPatientBlockFilters> = of({
      statuses: [PatientStatus.Lead],
      showDuplicate: false,
      showDeleted: false,
    });

    const queryResult$ = this._search
      .patientQuery$(search$, filters$, undefined, of(50), 'name:asc')
      .pipe(
        tap(() => this.loading$.next(false)),
        shareReplayCold()
      );

    this.recordCount$ = queryResult$.pipe(map((result) => result.numberFound));

    this.leads$ = queryResult$.pipe(map((result) => result.results));

    this.hasResults$ = this.leads$.pipe(map((leads) => leads.length > 0));
  }
}
